import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Checkbox } from '../Checkbox';
import styles from './ToolCategory.module.css';

export interface Tool {
  id: string;
  name: string;
  description?: string;
}

interface ToolCategoryProps {
  name: string;
  tools: Tool[];
  selectedTools: string[];
  onToolChange: (toolId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onAddIntegration?: () => void;
  className?: string;
}

export const ToolCategory: React.FC<ToolCategoryProps> = ({
  name,
  tools,
  selectedTools,
  onToolChange,
  onSelectAll,
  onAddIntegration,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedCount = useMemo(() => {
    return tools.filter(tool => selectedTools.includes(tool.id)).length;
  }, [tools, selectedTools]);

  const isAllSelected = selectedCount === tools.length && tools.length > 0;
  const isIndeterminate = selectedCount > 0 && selectedCount < tools.length;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSelectAll = (checked: boolean) => {
    onSelectAll(checked);
  };

  return (
    <div className={`${styles.category} ${className}`}>
      <button
        type="button"
        className={styles.header}
        onClick={toggleExpand}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerLeft}>
          <span className={styles.chevron}>
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </span>
          <span className={styles.name}>{name}</span>
        </div>
        {selectedCount > 0 && (
          <span className={styles.count}>{selectedCount}</span>
        )}
      </button>

      {isExpanded && (
        <div className={styles.content}>
          <div className={styles.toolsHeader}>
            <span className={styles.toolsLabel}>Features to use:</span>
          </div>

          <div className={styles.toolsList}>
            <div className={styles.selectAllRow}>
              <Checkbox
                label="Select all"
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
              />
            </div>

            {tools.map((tool) => (
              <div key={tool.id} className={styles.toolRow}>
                <Checkbox
                  label={tool.name}
                  checked={selectedTools.includes(tool.id)}
                  onChange={(checked) => onToolChange(tool.id, checked)}
                />
              </div>
            ))}
          </div>

          {onAddIntegration && selectedCount > 0 && (
            <button
              type="button"
              className={styles.addIntegrationButton}
              onClick={onAddIntegration}
            >
              <Plus size={16} />
              Add User Integration
            </button>
          )}
        </div>
      )}
    </div>
  );
};
